import { CommandProcessor } from "../engine/commands/CommandProcessor";
import { GameContext } from "../engine/core/GameContext";
import { CollisionSystem } from "../engine/systems/CollisionSystem";
import { InputSystem } from "../engine/systems/InputSystem";
import { PhysicsSystem } from "../engine/systems/PhysicsSystem";
import { RuleSystem } from "../engine/systems/RuleSystem";
import { SpatialGridSystem } from "../engine/systems/SpatialGridSystem";
import { BondState } from "../mechanics/bonds/BondState";
import { BondSystem } from "../mechanics/bonds/BondSystem";
import { BurstSystem } from "../mechanics/burst/BurstSystem";
import { PressureState } from "../mechanics/pressure/PressureState";
import { PressureSystem } from "../mechanics/pressure/PressureSystem";
import { BurstFeedback } from "../mechanics/feedback/BurstFeedback";
import type { ContentCatalog, CompiledLevel } from "../engine/content/types";
import { EventKind } from "../engine/events/EventQueue";
import { FIXED_STEP, MAX_FRAME_DELTA } from "../engine/core/constants";

export interface SceneLoadOptions {
  initialFill?: Readonly<Record<string, number>>;
  mechanisms?: { gravity: boolean; bonds: boolean; burst: boolean };
}
export interface RuntimeEvent {
  kind: number;
  source: number;
  target: number;
  x: number;
  y: number;
  value: number;
  step: number;
}

/** Shared simulation; consumers own display, HUD and telemetry. */
export class SceneRuntime {
  readonly context: GameContext;
  readonly inputSystem = new InputSystem();
  readonly gridSystem = new SpatialGridSystem();
  readonly physicsSystem = new PhysicsSystem();
  readonly collisionSystem = new CollisionSystem();
  readonly commandProcessor = new CommandProcessor();
  readonly pressureState = new PressureState();
  readonly bondState = new BondState();
  readonly bondSystem = new BondSystem(this.bondState, this.pressureState);
  readonly pressureSystem = new PressureSystem(
    this.pressureState,
    this.bondState,
  );
  readonly burstSystem = new BurstSystem(this.pressureState);
  readonly burstFeedback = new BurstFeedback();
  readonly ruleSystem = new RuleSystem();
  private accumulator = 0;
  private disposed = false;
  private initial?: CompiledLevel;
  private seed = 1;
  private loadOptions: SceneLoadOptions = {};
  private instanceIds = new Map<number, { identity: number; id: string }>();
  private eventListeners = new Set<(events: readonly RuntimeEvent[]) => void>();
  private listeners = new Set<
    (snapshot: ReturnType<SceneRuntime["snapshot"]>) => void
  >();
  speed = 1;
  mechanisms = { bonds: true, burst: true };
  constructor(
    readonly catalog: ContentCatalog,
    private readonly observe?: (context: GameContext) => void,
  ) {
    this.context = new GameContext(catalog);
  }
  private assertAlive() {
    if (this.disposed) throw new Error("运行时已销毁");
  }
  load(level: CompiledLevel, seed?: number, options: SceneLoadOptions = {}) {
    this.assertAlive();
    if (
      seed !== undefined &&
      (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff)
    )
      throw new Error("种子无效");
    for (const [id, fill] of Object.entries(options.initialFill || {})) {
      if (
        !level.entities.some((e) => e.instanceId === id) ||
        !Number.isFinite(fill) ||
        fill < 0 ||
        fill > 1
      )
        throw new Error("实例初始充盈度无效");
    }
    const c = this.context;
    this.loadOptions = structuredClone(options);
    this.mechanisms = options.mechanisms
      ? { ...options.mechanisms }
      : { bonds: true, burst: true };
    this.instanceIds.clear();
    this.initial = level;
    c.level = level;
    c.slimes.clear();
    this.bondState.clear();
    this.pressureState.clear();
    this.burstSystem.rings.ringCount = 0;
    c.resetTransientState();
    c.startAttempt();
    if (seed !== undefined) {
      c.state.seed = seed >>> 0;
      c.random.reset(c.state.seed);
    }
    this.seed = c.state.seed;
    c.state.playerX =
      c.state.targetPlayerX =
      c.input.pointerX =
        level.world.width / 2;
    c.input.pointerY = 160;
    this.accumulator = 0;
    for (const entity of level.entities) {
      const slot = c.slimes.spawn(entity.x, entity.y, entity.type);
      this.instanceIds.set(slot, {
        id: entity.instanceId,
        identity: c.slimes.recordingEntityId[slot],
      });
    }
    this.pressureState.syncSlots(c);
    for (const [slot, instance] of this.instanceIds) {
      const fill = options.initialFill?.[instance.id] ?? 0;
      const definition = this.catalog.slimes[c.slimes.definition[slot]],
        base = this.pressureState.restVolume(c, slot);
      this.pressureState.volume[slot] =
        base + (definition.burstVolume - base) * fill;
      c.slimes.radius[slot] =
        definition.radiusRest +
        (definition.radiusFull - definition.radiusRest) * fill;
    }
    c.state.fallOverride = options.mechanisms?.gravity === false ? 0 : 1;
    this.gridSystem.rebuild(c);
    this.ruleSystem.run(c, "levelStart");
    this.commandProcessor.flushEndOfStep(c);
  }
  reset() {
    this.assertAlive();
    this.context.state.score = 0;
    for (const key of Object.keys(
      this.context.metrics,
    ) as (keyof typeof this.context.metrics)[])
      this.context.metrics[key] = 0;
    if (this.initial) this.load(this.initial, this.seed, this.loadOptions);
  }
  nextTarget(): { id: string; resetScore: boolean } | null {
    const levels = [...this.catalog.levels]
      .filter((l) => !l.testFixture)
      .sort((a, b) => a.order - b.order);
    const phase = this.context.state.phase;
    if (phase === "game-over")
      return { id: this.context.level.id, resetScore: false };
    if (phase === "complete") return { id: levels[0].id, resetScore: true };
    if (phase === "level-clear") {
      const next = levels.find((l) => l.order > this.context.level.order);
      return next ? { id: next.id, resetScore: false } : null;
    }
    return null;
  }
  pause() {
    this.assertAlive();
    if (this.context.state.phase === "playing")
      this.context.state.phase = "paused";
    this.accumulator = 0;
    this.context.input.firing = false;
  }
  play() {
    this.assertAlive();
    if (this.context.state.phase === "paused")
      this.context.state.phase = "playing";
  }
  setSpeed(speed: number) {
    if (![0.25, 0.5, 1].includes(speed)) throw new Error("速度无效");
    this.speed = speed;
  }
  operate(input: { pointerX?: number; pointerY?: number; firing?: boolean }) {
    this.assertAlive();
    for (const key of ["pointerX", "pointerY"] as const) {
      const value = input[key];
      if (value !== undefined) {
        if (!Number.isFinite(value)) throw new Error("输入坐标无效");
        this.context.input[key] = value;
      }
    }
    if (input.firing !== undefined) {
      if (typeof input.firing !== "boolean") throw new Error("射击状态无效");
      this.context.input.firing = input.firing;
    }
  }
  step() {
    this.assertAlive();
    const state = this.context.state;
    if (!["playing", "paused"].includes(state.phase)) return;
    state.phase = "playing";
    state.hitstop = 0;
    this.fixedUpdate(FIXED_STEP);
    if (state.phase === "playing") state.phase = "paused";
    this.accumulator = 0;
  }
  tick(frameDelta: number) {
    this.assertAlive();
    const state = this.context.state;
    const realDt =
      Math.min(MAX_FRAME_DELTA, Math.max(0, frameDelta)) * this.speed;
    const dt = state.slowMotion > 0 ? realDt * 0.25 : realDt;
    if (state.phase !== "playing") return 0;
    state.slowMotion = Math.max(0, state.slowMotion - realDt);
    if (state.hitstop > 0) state.hitstop = Math.max(0, state.hitstop - dt);
    else {
      this.accumulator += dt;
      while (this.accumulator >= FIXED_STEP && state.phase === "playing") {
        this.fixedUpdate(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
      }
    }
    return dt;
  }
  subscribe(
    listener: (snapshot: ReturnType<SceneRuntime["snapshot"]>) => void,
  ) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  subscribeEvents(listener: (events: readonly RuntimeEvent[]) => void) {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }
  instanceId(slot: number) {
    const instance = this.instanceIds.get(slot);
    return instance?.identity === this.context.slimes.recordingEntityId[slot]
      ? instance.id
      : `runtime-${this.context.slimes.recordingEntityId[slot]}`;
  }
  snapshot() {
    const c = this.context,
      s = c.slimes;
    const slimes = [];
    for (let id = 0; id < s.capacity; id++)
      if (s.active[id])
        slimes.push({
          id: this.instanceId(id),
          slot: id,
          identity: s.recordingEntityId[id],
          definition: c.catalog.slimes[s.definition[id]].id,
          x: s.x[id],
          y: s.y[id],
          vx: s.vx[id],
          vy: s.vy[id],
          radius: s.radius[id],
          volume: this.pressureState.volume[id],
          pending: !!this.pressureState.pending[id],
          bindings: this.bondState.degree[id],
        });
    return {
      phase: c.state.phase,
      seed: c.state.seed,
      steps: c.state.steps,
      time: c.state.levelTime,
      levelId: c.level?.id,
      metrics: { ...c.metrics },
      slimes,
      drops: c.drops.activeCount,
      bonds: this.bondState.count,
    };
  }
  destroy() {
    if (this.disposed) return;
    this.disposed = true;
    this.listeners.clear();
    this.eventListeners.clear();
    this.instanceIds.clear();
    this.context.input.firing = false;
    this.context.slimes.clear();
    this.context.resetTransientState();
    this.bondState.clear();
    this.pressureState.clear();
  }
  fixedUpdate(dt: number): void {
    this.assertAlive();
    const context = this.context;
    this.inputSystem.update(context, dt);
    this.commandProcessor.flushLifecycle(context);
    this.ruleSystem.run(context, "beforePhysics");
    this.gridSystem.rebuild(context);
    this.physicsSystem.update(context, dt);
    this.ruleSystem.run(context, "afterPhysics");
    this.gridSystem.rebuild(context);
    this.collisionSystem.update(context);
    // Behavior Resolution：黏连先于压力，本步形成的绑定要计入本步的保压；爆浆最后，
    // 它读的是压力刚更新过的体积与待爆标记。
    if (this.mechanisms.bonds) this.bondSystem.update(context, dt);
    this.pressureSystem.update(context, dt);
    if (this.mechanisms.burst) this.burstSystem.update(context);
    context.metrics.maxRing = Math.max(
      context.metrics.maxRing,
      this.burstSystem.lastBurstWidth,
    );
    this.ruleSystem.run(context, "afterResolution");
    this.commandProcessor.flushResolution(context);
    this.burstFeedback.update(context);
    this.commandProcessor.flushLifecycle(context);
    this.ruleSystem.run(context, "afterFlush");
    const phaseBeforeOutcome = context.state.phase;
    this.commandProcessor.flushEndOfStep(context);
    if (phaseBeforeOutcome === "playing" && context.state.phase !== "playing")
      this.ruleSystem.run(context, "levelEnd");
    // 连锁归因：EntityDied 的 source 为引爆者，-1 即玩家灌到临界。平均连锁 C = kills / playerBursts。
    // value < 0 是越线结算的死亡，不是爆浆，不进连锁统计。
    for (let index = 0; index < context.events.count; index += 1) {
      if (context.events.kind[index] !== EventKind.EntityDied) continue;
      if (context.events.value[index] < 0) continue;
      context.metrics.kills += 1;
      if (context.events.source[index] < 0) context.metrics.playerBursts += 1;
    }
    this.observe?.(context);
    if (this.eventListeners.size) {
      const events = Array.from({ length: context.events.count }, (_, i) => ({
        kind: context.events.kind[i],
        source: context.events.source[i],
        target: context.events.target[i],
        x: context.events.x[i],
        y: context.events.y[i],
        value: context.events.value[i],
        step: context.state.steps,
      }));
      for (const listener of this.eventListeners) listener(events);
    }
    if (this.listeners.size) {
      const snapshot = this.snapshot();
      for (const listener of this.listeners) listener(snapshot);
    }
    context.events.clear();
  }
}
